<template>
  <div class="match3-game">
    <div class="game-header">
      <div class="header-left">
        <h2>Word Match 3</h2>
        <div class="category-title">{{ currentSetName }}</div>
        <div class="controls-row">
          <select v-model="currentSetIndex" @change="handleSetChange" class="set-selector">
            <option v-for="(set, index) in sets" :key="index" :value="index">
              {{ set.setName }} ({{ set.items.length }})
            </option>
          </select>
          <div class="language-selector">
            <button 
              v-for="lang in languages" 
              :key="lang.code"
              :class="{ active: currentLang === lang.code }"
              @click="currentLang = lang.code"
            >
              {{ lang.label }}
            </button>
          </div>
        </div>
      </div>
      <div class="score-box">
        <div class="score-label">Score</div>
        <div class="score-value">{{ score }}</div>
      </div>
    </div>

    <!-- High Score Announcement -->
    <transition name="fade">
      <div v-if="showAnnouncement" class="announcement">
        <div class="announcement-content">
          <h3>🎉 Congratulations! 🎉</h3>
          <p>High Score: {{ score }}</p>
          <p>Moving to next category...</p>
        </div>
      </div>
    </transition>

    <div class="game-area">
      <div class="game-board" ref="boardRef">
        <div v-for="(row, rowIndex) in board" :key="rowIndex" class="row">
          <div 
            v-for="(cell, colIndex) in row" 
            :key="`${rowIndex}-${colIndex}`" 
            class="cell"
            :class="{ selected: isSelected(rowIndex, colIndex) }"
            @click="handleCellClick(rowIndex, colIndex)"
          >
            <transition name="bounce">
              <div v-if="cell" class="tile" :class="`tile-${cell.id}`">
                <div class="tile-content">
                  <img :src="require(`@/assets/${cell.img}`)" :alt="cell.word" />
                  <span class="label">{{ getLocalizedWord(cell) }}</span>
                </div>
              </div>
            </transition>
          </div>
        </div>
      </div>

      <div class="side-panel">
        <div class="last-match-info" v-if="lastMatchedWord">
          <h3>Last Match</h3>
          <div class="match-display">
            <img :src="require(`@/assets/${lastMatchedWord.img}`)" :alt="lastMatchedWord.word" />
            <div class="word-details">
              <div class="main-word">{{ getLocalizedWord(lastMatchedWord) }}</div>
              <div class="sub-words">
                <div v-if="currentLang !== 'en'">{{ lastMatchedWord.en_string }}</div>
                <div v-if="currentLang !== 'cn'">{{ lastMatchedWord.cn_string }}</div>
                <div v-if="currentLang !== 'fr'">{{ lastMatchedWord.fr_string }}</div>
              </div>
            </div>
          </div>
        </div>
        
        <div class="controls">
          <button class="btn-new-game" @click="startNewGame">New Game</button>
        </div>
      </div>
    </div>

    <!-- Lyrics Display -->
    <div class="lyrics-display" v-if="currentLyric">
      <div class="lyric-line">{{ currentLyric }}</div>
    </div>

    <!-- SEO Content Section -->
    <div class="seo-content-section">
      <div class="seo-scrollview">
        <div class="seo-content">
          <h2>Word Match 3 - The Ultimate Educational Language Learning Game for Kids and Adults</h2>
          
          <h3>What is Word Match 3 Educational Game?</h3>
          <p>Word Match 3 is an innovative educational puzzle game that combines classic match-3 gameplay mechanics with comprehensive multilingual vocabulary learning across English, Chinese (中文), and French (Français). This free online learning game features 50 carefully curated vocabulary sets covering 500 essential words including fruits, vegetables, animals, colors, numbers, family members, body parts, clothing, household items, weather, transportation, food, drinks, sports, school supplies, nature, shapes, actions, emotions, sea animals, birds, insects, musical instruments, tools, kitchen items, toys, electronics, professions, time expressions, seasons, planets, directions, rooms, containers, metals, gemstones, desserts, grains, fabrics, hobbies, buildings, landforms, flowers, trees, size adjectives, quality adjectives, daily verbs, prepositions, and conjunctions. Perfect for language learners, ESL students, homeschooling families, kindergarten preparation, elementary education, vocabulary building, word recognition practice, and multilingual education programs.</p>
          
          <h3>Why Play Word Match 3 Language Learning Game?</h3>
          <p>Word Match 3 offers unparalleled educational value for vocabulary acquisition and language learning through engaging gameplay that keeps children and adults motivated to learn new words. Unlike traditional flashcard apps or boring vocabulary drills, this interactive educational game provides immersive learning experiences with visual word association, audio pronunciation practice in three languages, category-based learning organization, progressive difficulty levels, and instant feedback mechanisms. The game supports cognitive development, memory enhancement, pattern recognition skills, strategic thinking, multilingual proficiency, cultural awareness, and academic performance improvement. Ideal for preschool learning, kindergarten readiness, elementary school vocabulary expansion, middle school language arts, high school foreign language study, adult language learning, ESL/EFL instruction, special education programs, speech therapy exercises, dyslexia support, autism spectrum learning tools, and cognitive training for seniors. Parents love it for screen time that's actually educational, teachers use it for classroom engagement, and students enjoy learning without realizing they're studying.</p>
          
          <h3>How to Play Word Match 3 Educational Puzzle Game</h3>
          <p>Playing Word Match 3 is simple yet strategically engaging for all age groups and skill levels. Select from 50 themed vocabulary categories including everyday objects, academic subjects, nature topics, and language fundamentals. Choose your preferred learning language between English, Chinese, or French to customize your educational experience. Click adjacent tiles to swap positions and create matches of three or more identical word tiles horizontally or vertically. Each successful match teaches vocabulary through visual reinforcement, audio pronunciation, and written word recognition in multiple languages. The game features automatic tile dropping physics, cascading combo matches for bonus points, progressive scoring system with 300-point level completion threshold, automatic category advancement for continuous learning, background music synchronized with educational lyrics, real-time vocabulary display showing matched words with translations, and comprehensive pronunciation audio for every word in all three languages. Advanced features include random category selection for varied learning experiences, high score tracking and achievement celebrations, responsive design for desktop computers, tablets, and mobile phones, offline capability once loaded, no registration required for immediate play, completely free educational resource, ad-supported model for sustainability, and regular content updates with new vocabulary sets. Perfect for daily practice sessions, classroom activities, homework assignments, travel entertainment, waiting room activities, brain training exercises, family game nights, competitive learning challenges, and self-paced education programs.</p>
          
          <h3>Educational Benefits and Learning Outcomes</h3>
          <p>Word Match 3 delivers measurable educational benefits including expanded vocabulary knowledge across multiple languages, improved word recognition speed and accuracy, enhanced spelling skills through visual repetition, better pronunciation through audio modeling, increased reading comprehension abilities, stronger memory retention through game-based learning, developed categorization and classification skills, improved focus and concentration during gameplay, enhanced hand-eye coordination and motor skills, boosted confidence in language abilities, cultural literacy through multilingual exposure, and preparation for standardized testing vocabulary sections. Research shows that game-based learning increases student engagement by 60%, improves retention rates by 40%, and makes vocabulary acquisition 3x more effective than traditional memorization methods.</p>
          
          <h3>Perfect for Multiple Learning Scenarios</h3>
          <p>Word Match 3 excels in diverse educational settings including homeschool curriculum integration, classroom learning centers, computer lab activities, library programs, after-school enrichment, summer learning programs, tutoring sessions, special education interventions, gifted and talented programs, English as Second Language (ESL) instruction, English as Foreign Language (EFL) courses, Chinese language learning programs, French language immersion, bilingual education support, heritage language maintenance, study abroad preparation, international school curricula, Montessori learning environments, Waldorf education programs, and online distance learning platforms. The game adapts perfectly to individual learning paces, group activities, competitive tournaments, cooperative learning exercises, assessment tools, progress monitoring, differentiated instruction, universal design for learning (UDL) principles, and inclusive education practices.</p>
          
          <h3>Features That Make Learning Fun and Effective</h3>
          <p>Experience premium educational gaming features including 500 high-quality vocabulary words with professional translations, authentic native speaker pronunciation recordings, colorful visual representations for every word, intuitive touch and click controls, smooth animations and transitions, satisfying sound effects and feedback, progressive difficulty scaling, achievement celebrations and encouragement, category completion tracking, multilingual interface support, accessibility features for diverse learners, no time pressure for stress-free learning, unlimited replay value, regular content additions, cross-platform compatibility, responsive mobile-friendly design, fast loading times, minimal data usage, works on older devices, no downloads or installations required, instant browser-based play, bookmark-friendly URLs, shareable progress and achievements, printable vocabulary lists, teacher resources and lesson plans, parent guides and tips, and comprehensive help documentation.</p>
          
          <h3>Vocabulary Categories and Learning Themes</h3>
          <p>Master essential vocabulary across 50 comprehensive categories: Basic Concepts (Fruits, Vegetables, Colors, Numbers, Shapes), Living Things (Farm Animals, Wild Animals, Sea Animals, Birds, Insects, Flowers, Trees), Human World (Family Members, Body Parts, Emotions, Actions, Jobs, Hobbies), Daily Life (Clothes, Food, Drinks, House Items, Kitchen Tools, Toys, Electronics), Academic Subjects (School Supplies, Transportation, Weather, Nature, Seasons, Planets, Directions), Advanced Topics (Musical Instruments, Tools, Buildings, Landforms, Metals, Gemstones, Fabrics, Grains, Containers), and Language Fundamentals (Time Expressions, Rooms, Desserts, Size Adjectives, Quality Adjectives, Daily Verbs, Prepositions, Conjunctions). Each category contains 10 carefully selected high-frequency words essential for language proficiency and real-world communication.</p>
          
          <h3>Why Parents and Teachers Choose Word Match 3</h3>
          <p>Educators and parents worldwide trust Word Match 3 for quality educational content, safe child-friendly environment, no inappropriate advertisements, privacy-focused design with no data collection, alignment with educational standards and curricula, support for Common Core State Standards, compatibility with International Baccalaureate programs, reinforcement of Next Generation Science Standards vocabulary, preparation for TOEFL, IELTS, and other language proficiency tests, supplementary material for textbook learning, engaging alternative to traditional worksheets, measurable learning outcomes and progress, positive reinforcement and growth mindset encouragement, inclusive design for all learning styles (visual, auditory, kinesthetic), accommodation for special needs learners, differentiation opportunities for mixed-ability classrooms, easy integration into lesson plans, minimal teacher preparation required, self-directed learning capability, and proven effectiveness in vocabulary retention and language acquisition.</p>
          
          <h3>Start Your Language Learning Journey Today</h3>
          <p>Join thousands of successful language learners who have expanded their vocabulary, improved their language skills, and discovered the joy of learning through Word Match 3. Whether you're a student preparing for exams, a parent seeking quality educational content, a teacher looking for engaging classroom resources, an adult learning a new language, or simply someone who loves word games and puzzles, Word Match 3 provides the perfect combination of entertainment and education. Start playing now to experience immediate vocabulary growth, enhanced language confidence, improved academic performance, and lifelong learning benefits. No registration, no downloads, no waiting – just click and start learning! Perfect for quick 5-minute practice sessions or extended learning marathons. Make every moment a learning opportunity with Word Match 3, the free educational game that makes vocabulary learning fun, effective, and accessible to everyone, everywhere, on any device. Transform screen time into learning time and watch vocabulary skills soar!</p>
        </div>
      </div>
    </div>

    <!-- Hidden Audio Elements -->
    <audio ref="bgMusic" loop @timeupdate="updateLyrics"></audio>
    <audio ref="wordAudio"></audio>
  </div>
</template>

<script>
import gameData from '@/data/match3_data.json';

export default {
  name: 'Match3Game',
  data() {
    return {
      board: [],
      score: 0,
      gridSize: 8,
      sets: gameData,
      currentSetIndex: 0,
      selectedTile: null,
      isProcessing: false,
      currentLang: 'en',
      languages: [
        { code: 'en', label: 'English' },
        { code: 'cn', label: '中文' },
        { code: 'fr', label: 'Français' }
      ],
      lastMatchedWord: null,
      lyrics: [],
      currentLyric: '',
      showAnnouncement: false
    };
  },
  computed: {
    currentItems() {
      const currentSet = this.sets[this.currentSetIndex];
      return currentSet ? currentSet.items : [];
    },
    currentSetName() {
      const currentSet = this.sets[this.currentSetIndex];
      return currentSet ? currentSet.setName : '';
    }
  },
  mounted() {
    this.startNewGame();
  },
  watch: {
    score(newScore) {
      // Auto-progress to next set when score exceeds 300
      if (newScore >= 300 && !this.showAnnouncement) {
        this.handleHighScore();
      }
    }
  },
  methods: {
    handleHighScore() {
      this.showAnnouncement = true;
      
      // Wait 3 seconds then move to next set
      setTimeout(() => {
        this.showAnnouncement = false;
        this.moveToNextSet();
      }, 3000);
    },
    
    moveToNextSet() {
      // Move to next set (or wrap around)
      this.currentSetIndex = (this.currentSetIndex + 1) % this.sets.length;
      this.startNewGame();
    },
    
    handleSetChange() {
      this.startNewGame();
    },
    getLocalizedWord(item) {
      if (!item) return '';
      switch (this.currentLang) {
        case 'cn': return item.cn_string;
        case 'fr': return item.fr_string;
        default: return item.en_string;
      }
    },
    
    startNewGame() {
      // Random set selection on new game
      this.currentSetIndex = Math.floor(Math.random() * this.sets.length);
      this.score = 0;
      this.lastMatchedWord = null;
      this.showAnnouncement = false;
      this.initializeBoard();
      this.loadBackgroundMusic();
      this.loadLyrics();
    },

    loadBackgroundMusic() {
      const setName = this.currentSetName.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '');
      const musicPath = `/match3/music/${setName}.mp3`;
      
      // Test if file exists
      fetch(musicPath, { method: 'HEAD' })
        .then(response => {
          if (response.ok) {
            this.$refs.bgMusic.src = musicPath;
            this.$refs.bgMusic.play().catch(err => {
              console.log('Background music autoplay prevented:', err);
            });
          } else {
            console.log('Background music not found:', musicPath);
          }
        })
        .catch(err => {
          console.log('Background music not found:', musicPath);
        });
    },

    loadLyrics() {
      const setName = this.currentSetName.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '');
      const lrcPath = `/match3/lyrics/${setName}.lrc`;
      
      fetch(lrcPath)
        .then(response => {
          if (!response.ok) throw new Error('Lyrics not found');
          return response.text();
        })
        .then(lrcText => {
          this.lyrics = this.parseLRC(lrcText);
        })
        .catch(err => {
          console.log('Lyrics not found:', lrcPath);
          this.lyrics = [];
        });
    },

    parseLRC(lrcText) {
      const lines = lrcText.split('\n');
      const lyrics = [];
      
      lines.forEach(line => {
        const match = line.match(/\[(\d{2}):(\d{2})\.(\d{2,3})\](.*)/);
        if (match) {
          const minutes = parseInt(match[1]);
          const seconds = parseInt(match[2]);
          const milliseconds = parseInt(match[3]);
          const text = match[4].trim();
          const time = minutes * 60 + seconds + milliseconds / 1000;
          lyrics.push({ time, text });
        }
      });
      
      return lyrics.sort((a, b) => a.time - b.time);
    },

    updateLyrics() {
      if (!this.$refs.bgMusic || this.lyrics.length === 0) return;
      
      const currentTime = this.$refs.bgMusic.currentTime;
      let currentLyric = '';
      
      for (let i = 0; i < this.lyrics.length; i++) {
        if (currentTime >= this.lyrics[i].time) {
          currentLyric = this.lyrics[i].text;
        } else {
          break;
        }
      }
      
      this.currentLyric = currentLyric;
    },

    initializeBoard() {
      // Create initial board with no matches
      let newBoard;
      do {
        newBoard = Array(this.gridSize).fill(null).map(() => 
          Array(this.gridSize).fill(null).map(() => this.getRandomItem())
        );
      } while (this.hasMatches(newBoard));
      
      this.board = newBoard;
    },

    getRandomItem() {
      const items = this.currentItems;
      if (!items.length) return null;
      return { ...items[Math.floor(Math.random() * items.length)] };
    },

    isSelected(row, col) {
      return this.selectedTile && this.selectedTile.row === row && this.selectedTile.col === col;
    },

    async handleCellClick(row, col) {
      if (this.isProcessing) return;

      const clickedTile = this.board[row][col];
      if (clickedTile) {
        // Play sound for clicked tile
        this.playAudio(clickedTile);
      }

      if (!this.selectedTile) {
        // Select first tile
        this.selectedTile = { row, col };
      } else {
        // Select second tile
        const target = { row, col };
        
        // If clicked same tile, deselect
        if (this.selectedTile.row === row && this.selectedTile.col === col) {
          this.selectedTile = null;
          return;
        }

        // Check if adjacent
        if (this.isAdjacent(this.selectedTile, target)) {
          await this.swapTiles(this.selectedTile, target);
          
          if (this.hasMatches(this.board)) {
            await this.processMatches();
          } else {
            // Swap back if no match
            await new Promise(resolve => setTimeout(resolve, 300));
            await this.swapTiles(target, this.selectedTile);
          }
        }
        
        this.selectedTile = null;
      }
    },

    isAdjacent(t1, t2) {
      const dr = Math.abs(t1.row - t2.row);
      const dc = Math.abs(t1.col - t2.col);
      return (dr === 1 && dc === 0) || (dr === 0 && dc === 1);
    },

    async swapTiles(t1, t2) {
      const temp = this.board[t1.row][t1.col];
      // Use Vue.set or splice to ensure reactivity if needed, but direct assignment works in Vue 2 if replacing object properties, 
      // but for array indices we need splice or Vue.set. 
      // Here we are replacing the whole row array or using splice.
      // Let's use a deep copy approach for the board rows to trigger updates safely.
      
      // Simple swap logic
      const newBoard = [...this.board];
      newBoard[t1.row] = [...newBoard[t1.row]];
      newBoard[t2.row] = [...newBoard[t2.row]];
      
      newBoard[t1.row][t1.col] = this.board[t2.row][t2.col];
      newBoard[t2.row][t2.col] = temp;
      
      this.board = newBoard;
      
      // Wait for animation
      await new Promise(resolve => setTimeout(resolve, 300));
    },

    hasMatches(board) {
      // Check rows
      for (let r = 0; r < this.gridSize; r++) {
        for (let c = 0; c < this.gridSize - 2; c++) {
          if (board[r][c] && board[r][c+1] && board[r][c+2] &&
              board[r][c].id === board[r][c+1].id &&
              board[r][c].id === board[r][c+2].id) {
            return true;
          }
        }
      }
      // Check cols
      for (let r = 0; r < this.gridSize - 2; r++) {
        for (let c = 0; c < this.gridSize; c++) {
          if (board[r][c] && board[r+1][c] && board[r+2][c] &&
              board[r][c].id === board[r+1][c].id &&
              board[r][c].id === board[r+2][c].id) {
            return true;
          }
        }
      }
      return false;
    },

    async processMatches() {
      this.isProcessing = true;
      
      while (this.hasMatches(this.board)) {
        const matches = this.findMatches();
        if (matches.length === 0) break;

        // Update score and last matched word
        this.score += matches.length * 10;
        if (matches.length > 0) {
          const firstMatch = this.board[matches[0].row][matches[0].col];
          this.lastMatchedWord = firstMatch;
          this.playAudio(firstMatch);
        }

        // Remove matches
        const newBoard = this.board.map(row => [...row]);
        matches.forEach(({row, col}) => {
          newBoard[row][col] = null;
        });
        this.board = newBoard;
        
        await new Promise(resolve => setTimeout(resolve, 300));

        // Drop tiles
        await this.dropTiles();
        
        // Fill empty spaces
        await this.fillEmptySpaces();
      }
      
      this.isProcessing = false;
    },

    findMatches() {
      const matches = new Set();
      
      // Horizontal
      for (let r = 0; r < this.gridSize; r++) {
        for (let c = 0; c < this.gridSize - 2; c++) {
          const t1 = this.board[r][c];
          const t2 = this.board[r][c+1];
          const t3 = this.board[r][c+2];
          if (t1 && t2 && t3 && t1.id === t2.id && t1.id === t3.id) {
            matches.add(`${r},${c}`);
            matches.add(`${r},${c+1}`);
            matches.add(`${r},${c+2}`);
          }
        }
      }
      
      // Vertical
      for (let r = 0; r < this.gridSize - 2; r++) {
        for (let c = 0; c < this.gridSize; c++) {
          const t1 = this.board[r][c];
          const t2 = this.board[r+1][c];
          const t3 = this.board[r+2][c];
          if (t1 && t2 && t3 && t1.id === t2.id && t1.id === t3.id) {
            matches.add(`${r},${c}`);
            matches.add(`${r+1},${c}`);
            matches.add(`${r+2},${c}`);
          }
        }
      }
      
      return Array.from(matches).map(str => {
        const [r, c] = str.split(',');
        return { row: parseInt(r), col: parseInt(c) };
      });
    },

    async dropTiles() {
      const newBoard = this.board.map(row => [...row]);
      let moved = false;
      
      for (let c = 0; c < this.gridSize; c++) {
        for (let r = this.gridSize - 1; r > 0; r--) {
          if (newBoard[r][c] === null) {
            // Find nearest tile above
            for (let k = r - 1; k >= 0; k--) {
              if (newBoard[k][c] !== null) {
                newBoard[r][c] = newBoard[k][c];
                newBoard[k][c] = null;
                moved = true;
                break;
              }
            }
          }
        }
      }
      
      if (moved) {
        this.board = newBoard;
        await new Promise(resolve => setTimeout(resolve, 300));
      }
    },

    async fillEmptySpaces() {
      const newBoard = this.board.map(row => [...row]);
      let filled = false;
      
      for (let r = 0; r < this.gridSize; r++) {
        for (let c = 0; c < this.gridSize; c++) {
          if (newBoard[r][c] === null) {
            newBoard[r][c] = this.getRandomItem();
            filled = true;
          }
        }
      }
      
      if (filled) {
        this.board = newBoard;
        await new Promise(resolve => setTimeout(resolve, 300));
      }
    },

    playAudio(item) {
      if (!item || !this.$refs.wordAudio) return;
      
      const audioPath = item.mp3[this.currentLang];
      
      try {
        this.$refs.wordAudio.src = require(`@/assets/${audioPath}`);
        this.$refs.wordAudio.play().catch(err => {
          console.log('Audio playback prevented:', err);
        });
      } catch (err) {
        console.log('Audio file not found:', audioPath);
      }
    }
  }
};
</script>

<style scoped>
.match3-game {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 15px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  max-width: 900px;
  margin: 0 auto;
  user-select: none;
}

.game-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 20px;
  padding: 0 20px;
}

.header-left {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.controls-row {
  display: flex;
  gap: 15px;
  align-items: center;
  flex-wrap: wrap;
}

.set-selector {
  padding: 5px 10px;
  border-radius: 15px;
  border: 1px solid #ddd;
  font-size: 14px;
  background: white;
  color: #2c3e50;
  outline: none;
  cursor: pointer;
}

.header-left h2 {
  margin: 0;
  color: #2c3e50;
  font-size: 24px;
}

.language-selector {
  display: flex;
  gap: 5px;
}

.language-selector button {
  padding: 5px 10px;
  border: 1px solid #ddd;
  background: white;
  border-radius: 15px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s;
}

.language-selector button.active {
  background: #667eea;
  color: white;
  border-color: #667eea;
}

.score-box {
  background: #2c3e50;
  color: white;
  padding: 10px 20px;
  border-radius: 10px;
  text-align: center;
  min-width: 100px;
}

.score-label {
  font-size: 12px;
  opacity: 0.8;
  text-transform: uppercase;
}

.score-value {
  font-size: 24px;
  font-weight: bold;
}

.game-area {
  display: flex;
  gap: 30px;
  align-items: flex-start;
}

.game-board {
  display: flex;
  flex-direction: column;
  gap: 4px;
  background: #bbada0;
  padding: 8px;
  border-radius: 8px;
}

.row {
  display: flex;
  gap: 4px;
}

.cell {
  width: 65px;
  height: 65px;
  background: rgba(238, 228, 218, 0.35);
  border-radius: 6px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  position: relative;
}

.cell.selected {
  background: rgba(238, 228, 218, 0.6);
  box-shadow: inset 0 0 0 3px #667eea;
}

.tile {
  width: 100%;
  height: 100%;
  padding: 4px;
  animation: appear 0.3s ease-out;
}

.tile-content {
  width: 100%;
  height: 100%;
  background: white;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  transition: transform 0.2s;
}

.tile:hover .tile-content {
  transform: scale(1.05);
}

.tile img {
  width: 100%;
  height: 70%;
  object-fit: cover;
  margin-bottom: 2px;
}

.tile .label {
  font-size: 10px;
  color: #333;
  font-weight: bold;
  text-align: center;
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 0 2px;
}

.category-title {
  font-size: 18px;
  font-weight: bold;
  color: #667eea;
  margin: 5px 0;
}

.announcement {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.announcement-content {
  background: white;
  padding: 40px;
  border-radius: 20px;
  text-align: center;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  animation: scaleIn 0.3s ease-out;
}

.announcement-content h3 {
  font-size: 32px;
  margin: 0 0 20px 0;
  color: #667eea;
}

.announcement-content p {
  font-size: 18px;
  margin: 10px 0;
  color: #2c3e50;
}

@keyframes scaleIn {
  from {
    transform: scale(0.5);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}

.lyrics-display {
  margin-top: 20px;
  padding: 15px;
  background: rgba(102, 126, 234, 0.1);
  border-radius: 10px;
  text-align: center;
  min-height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.lyric-line {
  font-size: 16px;
  color: #2c3e50;
  font-weight: 500;
  line-height: 1.6;
}

/* SEO Content Section */
.seo-content-section {
  margin-top: 20px;
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 15px;
  width: 100%;
}

.seo-scrollview {
  height: 300px; /* 5x taller - approximately 15 lines visible */
  overflow-y: auto;
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 6px;
  padding: 10px;
}

.seo-content {
  color: rgba(44, 62, 80, 0.9);
  line-height: 1.6;
  font-size: 14px;
}

.seo-content h2 {
  font-size: 18px;
  margin-bottom: 15px;
  color: #2c3e50;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.1);
}

.seo-content h3 {
  font-size: 16px;
  margin: 20px 0 10px 0;
  color: #667eea;
  font-weight: bold;
}

.seo-content p {
  margin-bottom: 15px;
  text-align: justify;
  opacity: 0.95;
}

/* Custom scrollbar for SEO content */
.seo-scrollview::-webkit-scrollbar {
  width: 6px;
}

.seo-scrollview::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
}

.seo-scrollview::-webkit-scrollbar-thumb {
  background: rgba(102, 126, 234, 0.3);
  border-radius: 3px;
}

.seo-scrollview::-webkit-scrollbar-thumb:hover {
  background: rgba(102, 126, 234, 0.5);
}

.side-panel {
  width: 200px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.last-match-info {
  background: white;
  padding: 15px;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.05);
  text-align: center;
}

.last-match-info h3 {
  margin: 0 0 15px 0;
  font-size: 16px;
  color: #666;
}

.match-display img {
  width: 80px;
  height: 80px;
  object-fit: contain;
  margin-bottom: 10px;
}

.word-details .main-word {
  font-size: 20px;
  font-weight: bold;
  color: #2c3e50;
  margin-bottom: 5px;
}

.word-details .sub-words {
  font-size: 14px;
  color: #7f8c8d;
}

.btn-new-game {
  width: 100%;
  padding: 12px;
  background: #8f7a66;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 16px;
  font-weight: bold;
  transition: background 0.2s;
}

.btn-new-game:hover {
  background: #9e8b77;
}

@keyframes appear {
  from { opacity: 0; transform: scale(0.5); }
  to { opacity: 1; transform: scale(1); }
}

.bounce-enter-active {
  animation: bounce-in .5s;
}
.bounce-leave-active {
  animation: bounce-in .5s reverse;
}
@keyframes bounce-in {
  0% { transform: scale(0); }
  50% { transform: scale(1.2); }
  100% { transform: scale(1); }
}

@media (max-width: 768px) {
  .game-area {
    flex-direction: column;
    align-items: center;
  }
  
  .side-panel {
    width: 100%;
    flex-direction: row;
    justify-content: space-between;
  }
  
  .cell {
    width: 40px;
    height: 40px;
  }
  
  .tile .label {
    font-size: 8px;
  }
}
</style>
